import React, { useCallback, useEffect, useState } from "react";
import { debounce, Grow, IconButton, InputAdornment } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { TextFieldStyled } from "./TextFieldStyled";
import IconComponent from "../components/ui/IconComponent";
import { useTranslation } from "react-i18next";

interface Props {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
}

const SearchField = ({ value, onChange, disabled, placeholder, onFocus, onBlur }: Props) => {
  const [localValue, setLocalValue] = useState<string>(value);

  const { t } = useTranslation();

  const debouncedOnChange = useCallback(
    debounce((newValue: string) => {
      onChange(newValue);
    }, 200),
    [onChange]
  );

  const handleSearchValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalValue(e.target.value);
    debouncedOnChange(newValue);
  };

  const handleClear = () => {
    setLocalValue("");
    debouncedOnChange("");
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      (e.target as HTMLInputElement).blur();
    }
  };

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  return (
    <TextFieldStyled
      fullWidth
      sx={{ borderRadius: "12px" }}
      aria-autocomplete={"none"}
      autoComplete={"off"}
      variant="outlined"
      size="small"
      placeholder={placeholder ? placeholder : t("search")}
      value={localValue}
      onChange={handleSearchValue}
      onKeyDown={handleKeyDown}
      onBlur={onBlur}
      onFocus={onFocus}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <IconComponent iconName={"Search"} size={18} hasStroke hasFill />
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end">
            {localValue !== "" && (
              <Grow in={localValue !== ""}>
                <IconButton sx={{ width: 18 }} onClick={handleClear}>
                  <CloseIcon fontSize={"small"} />
                </IconButton>
              </Grow>
            )}
          </InputAdornment>
        ),
        sx: {
          borderRadius: "12px !important",
        },
      }}
    />
  );
};

export default SearchField;
